'use client';

import Image from 'next/image';
import { useParams } from 'next/navigation';
import { useEffect, useRef } from 'react';

import { COMMON_I18N } from '@/lib/i18n/commonI18n';
import type { AppLocale } from '@/lib/i18n/locales';
import { CLICK_REF_COOKIE, CLICK_REF_RESOLVED_EVENT } from '@/lib/clickRef';

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[2]) : null;
}

function buildWhatsappLink(
  phoneNumber: string,
  message: string,
  clickRef: string | null,
) {
  const digits = phoneNumber.replace(/\D/g, '');
  const text = clickRef ? `${message} [ref:${clickRef}]` : message;
  return `https://wa.me/${digits}/?text=${encodeURIComponent(text)}`;
}

const WhatsAppButton = ({
  phoneNumber,
  icon,
  ariaLabel,
}: WhatsappButtonProps) => {
  const { locale: raw } = useParams<{ locale?: string }>();
  const locale: AppLocale = raw === 'en' ? 'en' : 'pt';
  const message = COMMON_I18N[locale].whatsappGreeting;
  const anchorRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    const applyClickRef = () => {
      const clickRef = getCookie(CLICK_REF_COOKIE);
      if (clickRef && anchorRef.current) {
        anchorRef.current.href = buildWhatsappLink(
          phoneNumber,
          message,
          clickRef,
        );
      }
    };

    applyClickRef();
    window.addEventListener(CLICK_REF_RESOLVED_EVENT, applyClickRef);
    return () =>
      window.removeEventListener(CLICK_REF_RESOLVED_EVENT, applyClickRef);
  }, [phoneNumber, message]);

  return (
    <a
      ref={anchorRef}
      href={buildWhatsappLink(phoneNumber, message, null)}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={ariaLabel}
      className="fixed bottom-3 desktop:bottom-10 right-3 desktop:right-10 w-15 h-15 z-[1000]"
    >
      <div className="relative w-full h-full rounded-full flex items-center justify-center group">
        <span
          aria-hidden
          className="absolute inset-0 rounded-full bg-[#25d366] animate-pulse-ring group-hover:[animation-play-state:paused]"
        />
        <Image
          src={icon}
          alt=""
          width={60}
          height={60}
          className="relative transition-transform duration-300 group-hover:scale-110"
        />
      </div>
    </a>
  );
};

export default WhatsAppButton;

type WhatsappButtonProps = {
  phoneNumber: string;
  icon: string;
  ariaLabel: string;
};
